import { revalidateLogic } from '@tanstack/react-form';
import { FormattedMessage } from 'react-intl';
import { z } from 'zod';
import { SharedConfig } from '@_/config/shared-config.ts';
import { createFormMutationSubmitHandler, useFormMutation } from '@_/hooks';
import { buttonCommonI18n, labelCommonI18n } from '@_/i18n-common';
import { ErrorMessage, Heading, Text, VanillaLink } from '@_/ui';
import { BaseForm, FormSubmitButton, FormTextInput } from '../../formElements/index.ts';

const formSchema = z.object({
  email: z.email().max(SharedConfig.maxLength.email)
});

type FormSchema = z.input<typeof formSchema>;

export interface EmailStepProps {
  onNext: (data: { email: string }) => void;
  privacyPolicyUrl: string;
  termsOfUseUrl: string;
}

export function EmailStep({ onNext, privacyPolicyUrl, termsOfUseUrl }: EmailStepProps) {
  const form = BaseForm.useAppForm({
    defaultValues: {
      email: ''
    } as FormSchema,
    onSubmit: createFormMutationSubmitHandler(),
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'change'
    }),
    validators: {
      onDynamic: formSchema
    }
  });

  const { mutation, submit } = useFormMutation({
    form,
    formSchema,
    mutationOptions: {
      mutationFn: async data => {
        return onNext(data);
      }
    }
  });

  return (
    <div>
      <Heading level={2}>
        <FormattedMessage
          id="component:auth.heading.log-in-or-sign-up"
          defaultMessage="Log in or sign up in seconds"
        />
      </Heading>

      <form onSubmit={submit}>
        <div className="mt-6 flex flex-col gap-y-4">
          <Text block>
            <FormattedMessage
              id="component:auth.prompt.log-in-or-sign-up"
              defaultMessage="We'll check if you have an account and help create one if you don't."
            />
          </Text>

          {!!mutation.error && <ErrorMessage />}

          <div>
            <form.AppField
              name="email"
              children={() => (
                <FormTextInput autoFocus label={<FormattedMessage {...labelCommonI18n.email} />} />
              )}
            />
          </div>

          <div>
            <form.AppForm>
              <FormSubmitButton fillContainer size="lg" type="submit">
                <FormattedMessage {...buttonCommonI18n.continue} />
              </FormSubmitButton>
            </form.AppForm>
          </div>

          <Text block size="xs" subdued>
            <FormattedMessage
              id="component:auth.info.disclaimer"
              defaultMessage="By continuing, you agree to Slash How's <a1>Terms of Use</a1> and acknowledge the <a2>Privacy Policy</a2>."
              values={{
                a1: msg => (
                  <VanillaLink href={termsOfUseUrl} target="_blank">
                    {msg}
                  </VanillaLink>
                ),
                a2: msg => (
                  <VanillaLink href={privacyPolicyUrl} target="_blank">
                    {msg}
                  </VanillaLink>
                )
              }}
            />
          </Text>
        </div>
      </form>
    </div>
  );
}
